/**
 * Toast - glass surface with a muted status icon well (no accent left border).
 */

"use client";

import {
  Box,
  Flex,
  Icon,
  IconButton,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { type ReactNode } from "react";
import {
  FiAlertCircle,
  FiAlertTriangle,
  FiCheckCircle,
  FiInfo,
  FiX,
} from "react-icons/fi";
import { useThemeTokens } from "@/lib/theme/tokens";
import { useInteractiveTokens } from "@/lib/theme/tokens/interactive";
import { useClassicDashboardStyles } from "@/lib/hooks/useClassicDashboardStyles";
import { getPortaledSurfaceProps } from "@/lib/theme/portaled-surface";

export interface ToastProps {
  title?: ReactNode;
  description?: ReactNode;
  status?: "success" | "error" | "warning" | "info" | "loading";
  isClosable?: boolean;
  onClose?: () => void;
  icon?: ReactNode;
  duration?: number;
}

type ToastStatus = NonNullable<ToastProps["status"]>;

function getStatusStyles(
  status: ToastStatus,
  isDark: boolean,
  accentMuted: string,
  accentMutedBg: string,
) {
  const styles = {
    success: {
      icon: FiCheckCircle,
      wellBg: isDark ? "rgba(34, 197, 94, 0.14)" : "rgba(22, 163, 74, 0.1)",
      iconColor: isDark ? "#4ade80" : "#15803d",
    },
    error: {
      icon: FiAlertCircle,
      wellBg: isDark ? "rgba(239, 68, 68, 0.14)" : "rgba(220, 38, 38, 0.08)",
      iconColor: isDark ? "#f87171" : "#b91c1c",
    },
    warning: {
      icon: FiAlertTriangle,
      wellBg: isDark ? "rgba(245, 158, 11, 0.14)" : "rgba(217, 119, 6, 0.1)",
      iconColor: isDark ? "#fbbf24" : "#b45309",
    },
    info: {
      icon: FiInfo,
      wellBg: accentMutedBg,
      iconColor: accentMuted,
    },
    loading: {
      icon: null,
      wellBg: accentMutedBg,
      iconColor: accentMuted,
    },
  } as const;

  return styles[status] ?? styles.info;
}

export function Toast({
  title,
  description,
  status = "info",
  isClosable = true,
  onClose,
  icon,
}: ToastProps) {
  const theme = useThemeTokens();
  const interactive = useInteractiveTokens();
  const { cardBorderRadius, controlBorderRadius } = useClassicDashboardStyles();
  const surfaceProps = getPortaledSurfaceProps(theme, interactive, "toast");
  const isDark = theme.mode === "dark";

  const config = getStatusStyles(
    status,
    isDark,
    theme.accent.muted,
    theme.accent.mutedBg,
  );
  const IconComponent = config.icon;
  const hasDescription = description != null && description !== false;

  return (
    <Box
      data-testid={`toast-${status}`}
      w={{ base: "100%", md: "auto" }}
      minW={{ md: "300px" }}
      maxW={{ base: "100%", md: "380px" }}
      borderRadius={cardBorderRadius}
      borderWidth="1px"
      borderColor={theme.borderGlass.default}
      boxShadow={theme.shadow.card}
      p={{ base: 3.5, md: 4 }}
      color={theme.text.primary}
      transition="box-shadow 0.2s ease-out, border-color 0.2s ease-out"
      {...surfaceProps}
    >
      <Flex gap={3} align={hasDescription ? "flex-start" : "center"}>
        <Flex
          flexShrink={0}
          w="36px"
          h="36px"
          borderRadius={controlBorderRadius}
          align="center"
          justify="center"
          bg={config.wellBg}
          color={config.iconColor}
        >
          {icon ??
            (status === "loading" ? (
              <Spinner size="sm" color={config.iconColor} thickness="2.5px" />
            ) : IconComponent ? (
              <Icon as={IconComponent} boxSize="18px" />
            ) : null)}
        </Flex>

        <Box flex="1" minW={0} pt={hasDescription ? 0.5 : 0}>
          {title ? (
            <Text
              fontSize="sm"
              fontWeight="600"
              lineHeight="1.4"
              letterSpacing="-0.01em"
              color={theme.text.primary}
              sx={{
                "& .chakra-text": {
                  color: "inherit !important",
                },
              }}
            >
              {title}
            </Text>
          ) : null}
          {hasDescription ? (
            <Text
              as="div"
              fontSize="sm"
              fontWeight="400"
              lineHeight="1.5"
              color={theme.text.secondary}
              mt={title ? 1 : 0}
              sx={{
                "& .chakra-text": {
                  color: "inherit !important",
                },
              }}
            >
              {description}
            </Text>
          ) : null}
        </Box>

        {isClosable && onClose ? (
          <IconButton
            aria-label="Close toast"
            icon={<FiX />}
            size="xs"
            variant="ghost"
            onClick={onClose}
            flexShrink={0}
            alignSelf={hasDescription ? "flex-start" : "center"}
            color={theme.text.tertiary}
            borderRadius="8px"
            minW="28px"
            h="28px"
            sx={{
              "@media (hover: hover)": {
                "&:hover": {
                  bg: interactive.neutral.bg,
                  color: theme.text.secondary,
                },
              },
              "@media (hover: none) and (pointer: coarse)": {
                "&:active": {
                  bg: interactive.neutral.bg,
                },
              },
            }}
          />
        ) : null}
      </Flex>
    </Box>
  );
}
